import { SettlementEmailTemplate } from 'api/endpoints/settlementEmailTemplates';
import { snackInfoSuccessful } from 'components/SnackInfoNotification';
import { useApiQuery } from 'hooks/useApiQuery';
import { useCallback, useMemo, useState } from 'react';
import { VerticalTab } from '../../../../components/TabsVerticalPanel/TabsVerticalPanel';
import { apiSettlementEmailTemplates } from './apiSettlementEmailTemplates';

type MenuItem = VerticalTab<SettlementEmailTemplate>;
interface DialogWindow {
  title?: string;
  onConfirm: (confirm: boolean) => void;
}

interface DialogWindowRestore extends DialogWindow {
  subject: string;
  template: string;
}

const snackMessageSuccessful: Parameters<typeof snackInfoSuccessful>[0] = {
  title: 'Default Template Restored',
  message: (
    <>
      The default template for <strong>“Client Review Settlement”</strong> has been
      successfully restored.
    </>
  ),
};

export const useSettlementsEmailTemplates = () => {
  const [selectedId, setSelectedId] = useState(0);
  const [editedTemplate, setEditedTemplate] = useState<SettlementEmailTemplate>();
  const templatesQuery = useApiQuery({
    queryFn: () => apiSettlementEmailTemplates.get(),
  });
  const [loading, setLoading] = useState(false);

  const [dialogRestoreConfirm, setDialogRestoreConfirm] =
    useState<DialogWindowRestore>();
  const [dialogSaveConfirm, setDialogSaveConfirm] = useState<DialogWindow>();

  const selectedTemplate = templatesQuery.data?.[selectedId];

  const refreshData = useCallback(async () => {
    await templatesQuery.fetch();
    setEditedTemplate(undefined);
  }, [templatesQuery]);

  const menuList: MenuItem[] = useMemo(
    () =>
      templatesQuery.data?.map((r, id) => ({
        id,
        name: r.displayName,
        data: r,
      })) ?? [],
    [templatesQuery.data],
  );

  const setMenuSelected = useCallback((item: MenuItem) => {
    setSelectedId(item.id);
    setEditedTemplate(undefined);
  }, []);

  //===== Restore template =====================================================

  const restoreSelectedTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    setLoading(true);
    const result = await apiSettlementEmailTemplates.resetDefault(
      selectedTemplate.id,
    );
    setLoading(false);

    refreshData();

    if (result) snackInfoSuccessful(snackMessageSuccessful);
  }, [refreshData, selectedTemplate]);

  const restoreConfirm = useCallback(
    (confirm: boolean) => {
      setDialogRestoreConfirm(undefined);
      if (confirm) restoreSelectedTemplate();
    },
    [restoreSelectedTemplate],
  );

  const restoreClick = useCallback(async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    const result = await apiSettlementEmailTemplates.getDefaultTemplate(
      selectedTemplate.id,
    );
    setLoading(false);

    setDialogRestoreConfirm({
      subject: result?.defaultSubject ?? '',
      template: result?.defaultTemplate ?? '',
      onConfirm: restoreConfirm,
    });
  }, [restoreConfirm, selectedTemplate]);

  //===== Save template =====================================================

  const saveSelectedTemplate = useCallback(async () => {
    if (!editedTemplate) return;
    setLoading(true);
    await apiSettlementEmailTemplates.put(editedTemplate.id, editedTemplate);
    setLoading(false);

    refreshData();
  }, [editedTemplate, refreshData]);

  const saveConfirm = useCallback(
    (confirm: boolean) => {
      setDialogSaveConfirm(undefined);
      if (confirm) saveSelectedTemplate();
    },
    [saveSelectedTemplate],
  );

  const saveClick = useCallback(() => {
    if (!selectedTemplate) return;
    const name = selectedTemplate.displayName;
    const title = `Do you want to save the changes made to the template "${name}"?`;
    setDialogSaveConfirm({ title, onConfirm: saveConfirm });
  }, [saveConfirm, selectedTemplate]);

  //============================================================================

  return {
    menuList,
    menuSelected: menuList?.[selectedId],
    loading: templatesQuery.loading || loading,
    template: editedTemplate ?? selectedTemplate,
    canSave: !!editedTemplate && !!selectedTemplate,
    dialogRestoreConfirm,
    dialogSaveConfirm,
    setMenuSelected,
    change: setEditedTemplate,
    save: saveClick,
    restore: restoreClick,
  };
};
